/**
 * Permission matching for the UI. There are two kinds of question and they have
 * different answers:
 *
 * - **"May I do X to this thing?"** The backend ships the answer with the resource
 *   as `myPermissions`, already resolved across the global, team, share and self
 *   scopes — see `MyPermissions` in the OpenAPI doc. Use `has(resource, 'project:put')`.
 *   Nothing is derived here, so there is no scope logic left to drift out of sync.
 * - **"May I do X at all, or in this team?"** Nav-level and team-level questions that
 *   have no resource to hang off. Answered from the session payload's `permissions`
 *   and `teams[].permissions`. Use `can()` / `canInAnyTeam()`.
 *
 * Never gate on role names.
 */

/**
 * Type-only mirror of the backend Permission tree (permission.util.ts) for
 * autocomplete and compile-time checking. It carries no runtime data. Keep in
 * sync with the backend tree when permissions are added or removed.
 */
interface PermissionTree {
  global: {
    health: 'get';
    server_config: 'get' | 'put';
    sso_config: 'get' | 'put';
    user: 'post' | 'get' | 'patch' | 'delete';
    team: 'post' | 'get' | 'patch' | 'delete';
    folder: 'post' | 'get' | 'put' | 'delete' | 'share';
    project: 'post' | 'get' | 'put' | 'patch' | 'delete';
    inspection: 'post' | 'get' | 'put' | 'delete';
    poi_type: 'post' | 'get' | 'put' | 'delete' | 'share';
    app_settings: 'get' | 'patch';
    report_design: 'post' | 'get' | 'put' | 'delete';
    report_branding: 'get' | 'patch';
    statistic: 'get';
  };
  team: {
    team: 'get' | 'patch' | 'delete';
    user: 'get';
    folder: 'post' | 'get' | 'put' | 'delete' | 'share';
    project: 'post' | 'get' | 'put' | 'patch' | 'delete';
    inspection: 'post' | 'get' | 'put' | 'delete';
    poi_type: 'post' | 'get' | 'put' | 'delete' | 'share';
    app_settings: 'get' | 'patch';
    report_design: 'post' | 'get' | 'put' | 'delete';
    report_branding: 'get' | 'patch';
    statistic: 'get';
  };
  self: {
    inspection: 'post' | 'get' | 'put' | 'delete';
    statistic: 'get';
  };
}

export type PermissionScope = keyof PermissionTree;

/** Fully scoped permission strings, e.g. 'team:project:post'. */
export type AppPermission = {
  [S in PermissionScope]: {
    [C in keyof PermissionTree[S] & string]: `${S}:${C}:${PermissionTree[S][C] & string}`
  }[keyof PermissionTree[S] & string]
}[PermissionScope];

/** Scopeless permissions available under a given scope, e.g. ScopelessFor<'team'>. */
export type ScopelessFor<S extends PermissionScope> = AppPermission extends infer P
  ? P extends `${S}:${infer Scopeless}` ? Scopeless : never
  : never;

/** Scopeless permission strings, e.g. 'project:post'. */
export type ScopelessPermission = ScopelessFor<'global'> | ScopelessFor<'team'> | ScopelessFor<'self'>;

/** Anything the API returns with its own permission list attached. */
export interface PermissionedResource {
  myPermissions?: string[];
}

/**
 * Resource-level check. An absent permission means the matching request would be
 * rejected with 403, so this is safe to use both for gating controls and for
 * skipping requests that cannot succeed.
 */
export const has = (
  resource: PermissionedResource | null | undefined,
  permission: ScopelessPermission
): boolean => Boolean(resource?.myPermissions?.includes(permission));

export interface TeamGrant {
  id: string;
  role: string;
  permissions: string[];
}

export interface PermissionSubject {
  id: string;
  permissions?: string[];
  teams?: TeamGrant[];
}

/**
 * Session-level check for resources that carry no `myPermissions` of their own —
 * teams, users and server config, whose ownership is the resource id itself.
 * Pass `teamId` to ask within a specific team.
 */
export const can = (
  subject: PermissionSubject | undefined,
  permission: ScopelessPermission,
  teamId?: string
): boolean => {
  if (!subject) return false;

  if (subject.permissions?.includes(`global:${permission}`)) return true;

  return Boolean(teamId && subject.teams?.some(
    (team) => team.id === teamId && team.permissions.includes(`team:${permission}`)
  ));
};

/** Nav-level check: authorized globally or in at least one team. */
export const canInAnyTeam = (subject: PermissionSubject | undefined, permission: ScopelessFor<'team'>): boolean =>
  can(subject, permission)
  || Boolean(subject?.teams?.some((team) => team.permissions.includes(`team:${permission}`)));
